import { HttpClient } from '@angular/common/http';
import { stringify } from 'qs';
import { Injectable, OnDestroy } from '@angular/core';
import {
    catchError,
    filter,
    finalize,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import {
    DecodedToken,
    ImpersonationToken,
    isTokenResponse,
    logoutReason,
    MeInfo,
    SessionStore,
    TokenResponse
} from './session.store';
import { SbHost } from '@models';
import { UiService } from '../../../shared/state/ui/ui.service';
import { Router } from '@angular/router';
import { UiQuery } from '../../../shared/state/ui/ui.query';
import { SessionQuery } from './session.query';
import { concat, fromEvent, of, Subject, throwError, timer } from 'rxjs';
import { applyTransaction, filterNilValue, withTransaction } from '@datorama/akita';
import { AppConfigQuery } from '../app-config/app-config.query';
import { SbCacheService } from '@services';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '@environment';
import { ImpersonateStore } from '../../../pages/admin/impersonate/state/impersonate.store';
import { ImpersonateQuery } from 'src/app/pages/admin/impersonate/state/impersonate.query';
import jwtDecode from 'jwt-decode';
interface Credentials {
    username: string;
    password: string;
}
@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
    unsubscribe$ = new Subject();
    public credentials: Credentials = null;

    /** Refresh time margin - Time margin between token expiration and when token will be refreshed.
     * Eg. a value of 30 seconds (30 * 1000) will result in token being refreshed 30 seconds before it expires
     * if user has interacted with app. If user has interacted the token will be refreshed before it expires.
     */
    private _refreshTimeMargin = 30 * 1000;
    constructor(
        private sessionStore: SessionStore,
        private sessionQuery: SessionQuery,
        private http: HttpClient,
        private appConfig: AppConfigQuery,
        private ui: UiService,
        private uiQuery: UiQuery,
        private router: Router,
        private cacheService: SbCacheService,
        protected keycloak: KeycloakService,
        private impersonateStore: ImpersonateStore,
        private impersonateQuery: ImpersonateQuery //private impersonateService: ImpersonateService
    ) {
        // subscribe to "valid until" from session to determine when user should be logged
        this.sessionQuery.isAuthenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                tap((_) =>
                    // update me info once user is authenticated
                    this.getUserInfo().pipe(takeUntil(this.unsubscribe$)).subscribe()
                ),
                switchMap((_) =>
                    this.sessionQuery.validUntil$.pipe(
                        // when token expiration date changes, create timer by comparing expiration date/time with current date/time...
                        switchMap((tokenExpiration) =>
                            timer(
                                tokenExpiration -
                                    new Date().getTime() -
                                    this._refreshTimeMargin
                            )
                        ),
                        // ...when timer reaches zero (token has expired), check for user interaction...
                        withLatestFrom(this.sessionQuery.select('interaction')),
                        // ...if token has expired and user has interacted with the app since last cycle...
                        map(([timeLeft, interaction]) => timeLeft <= 0 && interaction),
                        switchMap(
                            (refresh) =>
                                refresh
                                    ? // ...refresh session using refresh token...
                                      this.refreshToken()
                                    : // ...else end session
                                      of(this.logout('sessionTimeOut')) // TODO: should use logout endpoint and return observable
                        ),
                        takeUntil(this.unsubscribe$)
                    )
                )
            )
            .subscribe();

        // listen for user interaction
        this.sessionQuery
            .select('interaction')
            .pipe(
                filter((interaction) => !interaction),
                switchMap((_) =>
                    fromEvent(document, 'click').pipe(
                        take(1),
                        tap((_) => this.addInteraction())
                    )
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        this.addInteraction();

        this.sessionStore.sessionChannel.onmessage = (event) => {
            if (
                event.origin === location.origin && // only accept messages from same origin
                event.data.payload.userId === this.sessionQuery.user.userId // only accept messages for current user
            ) {
                switch (event.data.payload.type) {
                    // handle logout messages
                    case 'LOGOUT':
                        this.logout(event.data.payload.trigger);
                        break;
                    // handle token exchange messages
                    case 'TOKEN_EXCHANGE':
                        location.reload();
                        break;
                }
            }
        };

        // listen for visibility changes... i.e. when user switches tabs or minimizes window
        this.uiQuery.isVisible$
            .pipe(
                filter((isVisible) => isVisible),
                // refresh token when user returns to app
                switchMap((_) => this.refreshToken()),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();
    }

    login(credentials: Credentials);
    login(otp: string);
    login(arg: string | Credentials) {
        let credentials;
        if (typeof arg === 'string') {
            credentials = { ...this.credentials, otp: arg };
        } else {
            this.credentials = { ...arg };
            credentials = { ...this.credentials };
        }
        const body = {
            grant_type: 'password',
            ...credentials
        };
        return this.performLogin(body);
    }

    refreshTokenLogin(refreshToken: string = this.sessionQuery.getRefreshToken()) {
        const body = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken
        };
        return this.performLogin(body);
    }

    private performLogin(grantBody: any) {
        applyTransaction(() => {
            this.sessionStore.setLoading(true);
            this.sessionStore.setLoadingState('loading');
        });
        return this.http
            .post<
                | TokenResponse
                | {
                      twoFactorRequired: {
                          primary?: string;
                          secondary?: string;
                      };
                  }
            >(
                `${this.appConfig.get().gatewayBaseUrl}/oauth/token`,
                stringify(grantBody),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: this.appConfig.get().basicAuthenticationHeader
                    }
                }
            )
            .pipe(
                filter((res) => {
                    // if otp is required, throw error to be caught by login component
                    if (!isTokenResponse(res) && !!res.twoFactorRequired) {
                        this.sessionStore.update((state) => ({
                            ...state,
                            twoFactorStatus: res.twoFactorRequired || {}
                        }));
                        throw res;
                    }
                    return isTokenResponse(res);
                }),
                withTransaction((session: TokenResponse) => {
                    const decodedToken = jwtDecode<DecodedToken>(session.access_token);
                    this.sessionStore.update((state) => ({
                        ...session,
                        decodedToken,
                        interaction: false
                    }));
                    this.sessionStore.setLoading(false);
                    this.sessionStore.setLoadingState('success');
                }),
                catchError((err) => {
                    applyTransaction(() => {
                        this.sessionStore.setLoading(false);
                        this.sessionStore.setLoadingState('error');
                    });
                    return throwError(err);
                }),
                switchMap((_) =>
                    this.http.get(
                        `${this.appConfig.getBaseUrlFromHost(
                            SbHost.LayoutConfigurationBaseUrl
                        )}/routes`
                    )
                ),
                tap((res: any) =>
                    this.ui.setLandingPage(
                        this.appConfig.appConfig.landingPageOverride ?? res.landingPage
                    )
                ),
                tap((_: any) => (this.credentials = null)),
                tap((_: any) => {
                    // if login route has url query param, redirect user to that url once logged in
                    let url =
                        this.router.parseUrl(this.router.url)?.queryParamMap.get('url') ||
                        null;
                    this.router.navigateByUrl(
                        url ??
                            // redirect user to admin page if user is admin
                            (this.sessionQuery.isAdminUser
                                ? '/admin'
                                : // redirect user to impersonation page if user is asset manager
                                this.sessionQuery.isAssetManager
                                ? '/impersonation'
                                : // else redirect user to landing page
                                  this.appConfig.appConfig.landingPageOverride || '')
                    );
                })
            );
    }

    refreshToken(refreshToken: string = this.sessionQuery.getRefreshToken()) {
        this.sessionStore.setLoading(true);
        const body = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken
        };
        const refreshTokenRequestQueue$ = [
            this.http
                .post<ImpersonationToken>(
                    `${this.appConfig.get().gatewayBaseUrl}/oauth/token`,
                    stringify(body),
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            Authorization: this.appConfig.get().basicAuthenticationHeader
                        }
                    }
                )
                .pipe(
                    withTransaction((session) => {
                        const decodedToken = jwtDecode<DecodedToken>(
                            session.access_token
                        );
                        this.sessionStore.update({
                            ...session,
                            decodedToken,
                            interaction: false
                        });
                        this.sessionStore.setLoading(false);
                    })
                )
        ];

        // if impersonation is active i.e. we have a refresh token for impersonation...
        if (this.impersonateQuery.refresh_token) {
            // ...push refresh impersonation session (token) request to refresh token queue
            refreshTokenRequestQueue$.push(this.refreshImpersonationSession());
        }

        return concat(...refreshTokenRequestQueue$);
    }

    addInteraction() {
        this.sessionStore.update((state) => ({ ...state, interaction: true }));
    }

    logout(trigger: logoutReason): void {
        // declare refresh token to use for logout
        const refreshToken = this.sessionQuery.getRefreshToken();

        if (this.appConfig.get().bypassRedirectUrl) {
            const reason = `reason=${trigger}`;
            const paramSeparator =
                this.appConfig.get().bypassRedirectUrl.indexOf('?') !== -1 ? '&' : '?';
            // redirect on next cycle (to allow store changes)
            setTimeout(
                () =>
                    (location.href =
                        this.appConfig.get().bypassRedirectUrl + paramSeparator + reason)
            );
        } else {
            if (this.appConfig.get().useLatestKeycloakFlow) {
                // is user actively logged out...
                if (trigger === 'userAction') {
                    // ...clear url from ui state to prevent user from ending up where they left off when logging in next time
                    this.ui.setUrl(null);
                }
                // Clear caches on logout - otherwise switching between admin and other users will cause issues with routes etc.
                this.cacheService.invalidateCaches();

                // reset session when user is logged out
                this.sessionStore.reset();

                // reset impersonate store on logged out
                this.impersonateStore.reset();
                this.keycloak
                    .logout(
                        environment.production
                            ? location.origin + `/${this.appConfig.get().keycloakRealm}`
                            : location.origin
                    )
                    .then(() => {
                        // broadcast logout message to all tabs
                        this.sessionStore.sessionChannel.postMessage({
                            payload: {
                                type: 'LOGOUT',
                                userId: this.sessionQuery.user.userId,
                                trigger
                            }
                        });

                        // redirect user to login screen and force refresh to clear app memory and re-trigger app.component
                        location.href = `./login?reason=${trigger}`;
                    });
            } else if (refreshToken) {
                this.http
                    .post(
                        `${this.appConfig.get().gatewayBaseUrl}/oauth/logout`,
                        refreshToken
                    )
                    .pipe(
                        finalize(() => {
                            // TODO: use storage key form other branch
                            const sessionStorageKey = this.sessionQuery.sessionStorageKey;

                            // broadcast logout message to all tabs
                            this.sessionStore.sessionChannel.postMessage({
                                payload: {
                                    type: 'LOGOUT',
                                    userId: this.sessionQuery.user.userId,
                                    trigger
                                }
                            });
                            // is user actively logged out...
                            if (trigger === 'userAction') {
                                // ...clear url from ui state to prevent user from ending up where they left off when logging in next time
                                this.ui.setUrl(null);
                            }
                            // Clear caches on logout - otherwise switching between admin and other users will cause issues with routes etc.
                            this.cacheService.invalidateCaches();

                            // get storage key for user
                            const storageKey = `${this.sessionQuery.sessionStorageKey}_sbResumeUrl`;

                            // reset session when user is logged out
                            this.sessionStore.reset();

                            // reset impersonate store on logged out
                            this.impersonateStore.reset();

                            // if session times out...
                            if (trigger === 'sessionTimeOut') {
                                // ...store current url in session storage to resume user to the same page after login
                                sessionStorage.setItem(storageKey, btoa(this.router.url));
                            }

                            // clear session storage for user
                            sessionStorage.removeItem(sessionStorageKey);

                            // redirect user to login screen and force refresh to clear app memory and re-trigger app.component
                            location.href = `./login?reason=${trigger}`;
                        })
                    )
                    .subscribe();
            } else {
                // if no refresh token is found, redirect user to login screen
                location.href = `./login`;
            }
        }
    }

    getUserInfo() {
        return this.appConfig.select('gatewayBaseUrl').pipe(
            filterNilValue(),
            switchMap((gatewayUrl) =>
                this.http
                    .get(`${gatewayUrl}/users/me`, {
                        headers: {
                            Authorization: `Bearer ${this.sessionQuery.access_token}`
                        }
                    })
                    .pipe(
                        withTransaction((user: MeInfo) => {
                            this.sessionStore.update((state) => ({
                                ...state,
                                user
                            }));
                        })
                    )
            )
        );
    }

    refreshImpersonationSession(
        refreshToken: string = this.impersonateQuery.refresh_token
    ) {
        const body = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken
        };
        return this.http
            .post<ImpersonationToken>(
                `${this.appConfig.get().gatewayBaseUrl}/oauth/token`,
                stringify(body),
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: this.appConfig.get().basicAuthenticationHeader
                    }
                }
            )
            .pipe(
                withTransaction((token) => {
                    const decodedToken = jwtDecode<DecodedToken>(token.access_token);
                    this.impersonateStore.update((state) => ({
                        ...state,
                        token,
                        decodedToken
                    }));
                })
            );
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
